import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Clock, Euro, Coffee, EyeOff, GripVertical, Pause, Play } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const EMPTY = { name: "", category: "", description: "", price: "", duration_min: "", rest_min: "", load: 0, schedulable: true, active: true, paused: false, order: 0 };

export default function Treatments() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [dragId, setDragId] = useState(null);

  const load = useCallback(() => api.get("/admin/treatments").then((r) => setList(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const persistOrder = async (ordered) => {
    setList(ordered);
    try { await api.put("/admin/treatments/reorder", { ids: ordered.map((t) => t.id) }); }
    catch { toast.error("Volgorde opslaan mislukt."); load(); }
  };

  const onDropRow = (target) => {
    if (!dragId || dragId === target.id) { setDragId(null); return; }
    const dragged = list.find((t) => t.id === dragId);
    if (!dragged || dragged.category !== target.category) { setDragId(null); return; }
    const without = list.filter((t) => t.id !== dragId);
    const idx = without.findIndex((t) => t.id === target.id);
    without.splice(idx, 0, dragged);
    setDragId(null);
    persistOrder(without);
  };

  const categories = [...new Set(list.map((t) => t.category).filter(Boolean))];

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, category: t.category, description: t.description || "",
      price: t.price, duration_min: t.duration_min, rest_min: t.rest_min || 0, load: t.load || 0,
      schedulable: t.schedulable !== false, active: t.active !== false, paused: t.paused === true, order: t.order || 0 });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.category || form.price === "" || form.duration_min === "") {
      toast.error("Naam, categorie, prijs en duur zijn verplicht."); return;
    }
    const payload = {
      name: form.name, category: form.category, description: form.description,
      price: Number(form.price), duration_min: Number(form.duration_min), rest_min: Number(form.rest_min || 0), load: Number(form.load || 0),
      schedulable: form.schedulable, active: form.active, paused: form.paused, order: Number(form.order || 0),
    };
    try {
      if (editing) await api.put(`/admin/treatments/${editing.id}`, payload);
      else await api.post("/admin/treatments", payload);
      toast.success(editing ? "Behandeling bijgewerkt." : "Behandeling toegevoegd.");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Behandeling "${t.name}" verwijderen?`)) return;
    await api.delete(`/admin/treatments/${t.id}`);
    toast.success("Behandeling verwijderd."); load();
  };

  const togglePause = async (t) => {
    const paused = !t.paused;
    try {
      await api.put(`/admin/treatments/${t.id}/pause`, { paused });
      setList((prev) => prev.map((x) => (x.id === t.id ? { ...x, paused } : x)));
      toast.success(paused ? "Behandeling tijdelijk gestaakt — klanten zien 'Tijdelijk niet mogelijk'." : "Behandeling weer beschikbaar om te reserveren.");
    } catch { toast.error("Wijzigen mislukt."); }
  };

  const grouped = {};
  list.forEach((t) => { (grouped[t.category] ||= []).push(t); });

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <h1 className="text-3xl font-medium tracking-tight">Behandelingen</h1>
        <Button onClick={openNew} data-testid="add-treatment-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">
          <Plus className="w-4 h-4 mr-1" /> Nieuwe behandeling
        </Button>
      </div>
      <p className="text-ink-muted mb-8">Deze behandelingen staan op de website en zijn te plannen in de agenda.</p>

      {!list.length && <div className="bg-white rounded-2xl border border-[#E8E2D9] p-8 text-center text-ink-muted text-sm">Nog geen behandelingen.</div>}

      <div className="space-y-6">
        {Object.entries(grouped).map(([cat, items]) => (
          <div key={cat} className="bg-white rounded-2xl border border-[#E8E2D9] shadow-[0_8px_30px_rgb(74,63,63,0.04)] overflow-hidden">
            <div className="px-5 py-3 text-xs uppercase tracking-wide text-brand border-b border-[#F2EBE1]">{cat}</div>
            <div className="divide-y divide-[#F2EBE1]">
              {items.map((t) => (
                <div key={t.id} data-testid={`treatment-${t.id}`}
                  draggable onDragStart={() => setDragId(t.id)} onDragEnd={() => setDragId(null)}
                  onDragOver={(e) => e.preventDefault()} onDrop={() => onDropRow(t)}
                  className={`flex items-center gap-3 p-4 hover:bg-cream-subtle/40 transition-colors ${dragId === t.id ? "opacity-50" : ""}`}>
                  <span className="cursor-grab active:cursor-grabbing text-ink-muted hover:text-brand" data-testid={`drag-treatment-${t.id}`}><GripVertical className="w-4 h-4" /></span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{t.name}</span>
                      {t.paused && <span className="text-[10px] flex items-center gap-1 text-amber-700 bg-amber-100 rounded-full px-2 py-0.5" data-testid={`paused-badge-${t.id}`}><Pause className="w-3 h-3" /> Tijdelijk niet mogelijk</span>}
                      {t.active === false && <span className="text-[10px] flex items-center gap-1 text-ink-muted bg-[#F2EBE1] rounded-full px-2 py-0.5"><EyeOff className="w-3 h-3" /> Verborgen</span>}
                    </div>
                    {t.description && <div className="text-sm text-ink-muted line-clamp-1">{t.description}</div>}
                    <div className="flex flex-wrap gap-x-4 text-sm text-ink-muted mt-0.5">
                      <span className="flex items-center gap-1"><Euro className="w-3 h-3" /> {Number(t.price).toFixed(2)}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t.duration_min} min</span>
                      <span className="flex items-center gap-1"><Coffee className="w-3 h-3" /> {t.rest_min || 0} min rust</span>
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="icon" variant="ghost" onClick={() => togglePause(t)} data-testid={`pause-treatment-${t.id}`} title={t.paused ? "Weer beschikbaar maken" : "Tijdelijk staken"}>
                      {t.paused ? <Play className="w-4 h-4 text-emerald-600" /> : <Pause className="w-4 h-4 text-amber-600" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-treatment-${t.id}`}><Pencil className="w-4 h-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(t)} data-testid={`delete-treatment-${t.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-w-lg" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Behandeling bewerken" : "Nieuwe behandeling"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-auto no-scrollbar pr-1">
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Naam *</Label><Input data-testid="treat-name" value={form.name} onChange={set("name")} className="rounded-xl border-[#E8E2D9]" /></div>
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Categorie *</Label>
              <Input data-testid="treat-category" value={form.category} onChange={set("category")} list="cat-list" placeholder="bijv. Massage" className="rounded-xl border-[#E8E2D9]" />
              <datalist id="cat-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>
            </div>
            <div><Label className="text-sm text-ink-muted mb-1.5 block">Omschrijving <span className="text-ink-muted/60">(zichtbaar op website)</span></Label><Textarea data-testid="treat-description" value={form.description} onChange={set("description")} rows={3} className="rounded-xl border-[#E8E2D9]" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-sm text-ink-muted mb-1.5 block">Prijs (€) *</Label><Input data-testid="treat-price" type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="rounded-xl border-[#E8E2D9]" /></div>
              <div><Label className="text-sm text-ink-muted mb-1.5 block">Duur (min) *</Label><Input data-testid="treat-duration" type="number" min="5" step="5" value={form.duration_min} onChange={set("duration_min")} className="rounded-xl border-[#E8E2D9]" /></div>
              <div><Label className="text-sm text-ink-muted mb-1.5 block">Rust (min)</Label><Input data-testid="treat-rest" type="number" min="0" step="5" value={form.rest_min} onChange={set("rest_min")} className="rounded-xl border-[#E8E2D9]" /></div>
            </div>
            <div>
              <Label className="text-sm text-ink-muted mb-1.5 block">Belasting (voor planningsassistent)</Label>
              <div className="flex gap-1.5">
                {[[0, "Auto"], [1, "1 · Licht"], [2, "2"], [3, "3 · Normaal"], [4, "4"], [5, "5 · Zwaar"]].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`treat-load-${v}`} onClick={() => setForm({ ...form, load: v })}
                    className={`flex-1 rounded-lg py-2 text-xs border transition-colors ${Number(form.load) === v ? "bg-brand text-white border-brand" : "bg-white text-ink-muted border-[#E8E2D9]"}`}>{l}</button>
                ))}
              </div>
              <p className="text-xs text-ink-muted mt-1">"Auto" bepaalt de belasting op basis van de duur. Zet hoger voor mentaal zware behandelingen (bv. massage).</p>
            </div>
            <div className="rounded-xl border border-[#E8E2D9] divide-y divide-[#F2EBE1]">
              <div className="flex items-center justify-between p-3">
                <div className="text-sm">Zichtbaar op website</div>
                <Switch data-testid="treat-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              </div>
              <div className="flex items-center justify-between p-3">
                <div className="text-sm">Inplanbaar in agenda</div>
                <Switch data-testid="treat-schedulable" checked={form.schedulable} onCheckedChange={(v) => setForm({ ...form, schedulable: v })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Annuleren</Button>
            <Button onClick={save} data-testid="save-treatment-btn" className="rounded-full bg-brand hover:bg-brand-hover text-white">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
